<template>
  <div v-if="!hasTranslation && emptyText" class="message-preview__empty">
    {{ emptyText }}
  </div>
  <div v-else class="message-preview" :style="wrapperStyles">
    <chatlyn-spinner :is-visible="isRendering" />
    <iframe
      ref="templatePreview"
      :src="iframeSrc"
      :class="isRendering ? 'opacity-0' : 'opacity-100'"
      height="100%"
      width="100%"
    />
  </div>
</template>
<script>
import { mapGetters } from 'vuex';
import { setColorTheme } from 'dashboard/helper/themeHelper.js';
import ChatlynSpinner from 'shared/components/ChatlynSpinner.vue';
import { MESSAGE_TEMPLATES_CHANNEL_TYPE } from '../constants';
import {
  buildPhoenixPreviewPayload,
  normalizePhoenixTranslation,
} from 'dashboard/helper/phoenixTemplatePreviewAdapter';

export default {
  name: 'TemplateMessagePreview',
  components: {
    ChatlynSpinner,
  },
  props: {
    currentTranslation: {
      type: Object,
      default: () => ({}),
    },
    templateType: {
      type: String,
      default: MESSAGE_TEMPLATES_CHANNEL_TYPE.WHATSAPP,
    },
    mediaUrl: {
      type: String,
      default: '',
    },
    emptyText: {
      type: String,
      default: '',
    },
    // TO DO: remove after adjust and connect template builder in whatsapp bot config
    editMode: {
      type: Boolean,
      default: false,
    },
    useSentParams: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      isRendering: true,
      templateHeight: 0,
    };
  },
  computed: {
    ...mapGetters({
      isDark: 'theme/isDark',
      isPhoenix: 'accounts/isPhoenix',
    }),
    iframeSrc() {
      const baseUrl =
        process.env.FRONTEND_ENV === 'develop'
          ? process.env.STAGING_FRONTEND_URL
          : window.location.origin;
      return (
        baseUrl + `/automation-studio/templates/view?type=${this.templateType}`
      );
    },
    previewSentParams() {
      if (!this.useSentParams || !this.isPhoenix) {
        return null;
      }

      const components = this.currentTranslation?.components || [];
      return buildPhoenixPreviewPayload({ components }, components) || null;
    },
    previewTemplateTranslation() {
      if (!this.currentTranslation || !Object.keys(this.currentTranslation).length) {
        return this.currentTranslation;
      }

      if (this.isPhoenix) {
        return normalizePhoenixTranslation(
          this.currentTranslation,
          this.previewSentParams
        );
      }

      return this.currentTranslation;
    },
    translationId() {
      // eslint-disable-next-line no-underscore-dangle
      return (
        this.previewTemplateTranslation?._id ??
        this.previewTemplateTranslation?.id ??
        ''
      );
    },
    hasTranslation() {
      if (this.editMode) {
        const { components = [] } = this.previewTemplateTranslation || {};
        return components.some((component) => {
          return (
            component?.text || component.buttons?.some((button) => button?.text)
          );
        });
      }
      return !!this.translationId;
    },
    wrapperStyles() {
      return {
        height: this.templateHeight ? this.templateHeight + 'px' : 'auto',
      };
    },
    previewMediaUrl() {
      if (this.useSentParams && this.previewSentParams?.media) {
        return this.previewSentParams.media;
      }

      return this.mediaUrl;
    },
  },
  watch: {
    isDark: {
      handler(val) {
        const iframeEl = this.$refs.templatePreview;
        setColorTheme(val, iframeEl?.contentDocument);
      },
      immediate: true,
    },
    translationId() {
      this.sentCurrentTranslation();
    },
    mediaUrl() {
      if (this.useSentParams && this.isPhoenix) {
        return;
      }
      this.sentCurrentTranslation();
    },
    previewMediaUrl() {
      if (!this.useSentParams || !this.isPhoenix) {
        return;
      }
      this.sentCurrentTranslation();
    },
    previewSentParams: {
      handler() {
        if (this.useSentParams && this.isPhoenix) {
          this.sentCurrentTranslation();
        }
      },
      deep: true,
    },
    currentTranslation() {
      this.sentCurrentTranslation();
    },
  },
  mounted() {
    window.addEventListener('message', this.onReceiveMessage);
  },
  destroyed() {
    window.removeEventListener('message', this.onReceiveMessage);
  },
  methods: {
    sentCurrentTranslation() {
      const normalizedTranslation = this.previewTemplateTranslation;

      if (!normalizedTranslation) {
        return;
      }

      const data = {
        event: 'onMessagePreview',
        payload: {
          templateTranslation: normalizedTranslation,
          mediaUrl: this.previewMediaUrl,
        },
      };

      if (this.useSentParams && this.isPhoenix && this.previewSentParams) {
        data.payload.sentParams = this.previewSentParams;
      }

      this.sendPostMessage(data);
    },
    onReceiveMessage({ data, source }) {
      const iframeWindow = this.$refs.templatePreview?.contentWindow;
      if (source !== iframeWindow) return;
      if (data.event === 'onIframeLoad') {
        this.sentCurrentTranslation();
      }
      if (data.event === 'onPreviewRendered') {
        this.setHeight(data.payload ?? {});
      }
    },
    setHeight({ height = 0 }) {
      this.templateHeight = height ?? 0;
      this.$nextTick(() => {
        this.isRendering = false;
      });
    },
    sendPostMessage(data) {
      const iframeEl = this.$refs.templatePreview;
      if (iframeEl && iframeEl?.contentWindow) {
        iframeEl.contentWindow.postMessage(data, '*');
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.message-preview {
  @apply pointer-events-none relative w-full select-none transition-all duration-300;
  &__empty {
    @apply flex aspect-video w-full items-center justify-center rounded border bg-gray-50 px-2 py-4 text-center text-sm text-gray-500 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-400;
  }
}
</style>
